import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { CONTACT, WHATSAPP_URL } from "@/data/site";
import Logo from "@/components/Logo";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Pricing", id: "pricing" },
  { label: "Offers", id: "offers" },
  { label: "Book a Pickup", id: "book" },
  { label: "Contact", id: "contact" },
];

const Header = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-background/90 backdrop-blur-md border-b border-foreground/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="/" data-testid="logo-link" className="text-foreground">
            <Logo size="md" />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm font-medium tracking-wide text-foreground/70 hover:text-foreground transition-colors"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/commercial"
              data-testid="nav-commercial"
              className="text-sm font-medium tracking-wide text-foreground hover:text-foreground/60 transition-colors underline underline-offset-4 decoration-foreground/30"
            >
              Commercial
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="hidden md:flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              data-testid="header-call"
            >
              <Phone size={15} /> {CONTACT.phone}
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp"
              className="group relative hidden sm:inline-flex items-center gap-2 border border-foreground px-4 py-2.5 text-xs md:text-sm font-medium uppercase tracking-widest overflow-hidden"
            >
              <MessageCircle size={15} className="relative z-10 transition-colors duration-300 group-hover:text-background" />
              <span className="relative z-10 transition-colors duration-300 group-hover:text-background">WhatsApp</span>
              <span className="absolute inset-0 bg-foreground translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </a>
            <button
              data-testid="header-book-btn"
              onClick={onBook}
              className="group relative bg-foreground text-background px-5 py-2.5 text-xs md:text-sm font-medium uppercase tracking-widest overflow-hidden"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-foreground">Book a Pickup</span>
              <span className="absolute inset-0 bg-background translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
            <button
              className="lg:hidden p-2"
              onClick={() => setOpen(true)}
              data-testid="menu-open"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-foreground text-background flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="h-16 md:h-20 flex items-center justify-between px-5 md:px-10">
              <Logo size="sm" />
              <button onClick={() => setOpen(false)} data-testid="menu-close" aria-label="Close menu">
                <X size={26} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-5xl md:text-6xl font-light hover:text-background/60 transition-colors"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + LINKS.length * 0.07 }}
              >
                <Link
                  to="/commercial"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-commercial"
                  className="text-left font-serif text-5xl md:text-6xl font-light text-background underline underline-offset-4 decoration-background/30 hover:text-background/60 transition-colors"
                >
                  Commercial
                </Link>
              </motion.div>
              <motion.a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-whatsapp"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + (LINKS.length + 1) * 0.07 }}
                className="mt-6 inline-flex items-center gap-3 border border-background px-6 py-4 text-sm font-medium uppercase tracking-widest w-fit hover:bg-background hover:text-foreground transition-colors"
              >
                <MessageCircle size={18} /> Book on WhatsApp
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
